/**
 * TodayWithDraper - the top of Draper's home: a greeting for the time of day, what the team did today in plain sentences,
 * what is waiting for the owner's OK, and the reminders/routines coming up.
 *
 * Reuses what exists: the activity feed (passed in from the page, same query) and the routines endpoints that Settings ›
 * Routines uses (GET /routines, POST /routines/parse, POST /routines). Approve/reject stays in the Feed panel ("Review"
 * opens it), so the publish gates are unchanged. A new reminder is shown back to the owner and only saved on "Looks right".
 * Two shapes: "column" (wide screens, a right-hand home feed beside the chat) and "strip" (narrow screens, folds above the chat).
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { ChevronDown, ChevronUp, Clock, Bell, Loader2, Plus, X } from "lucide-react";
import type { DraperRoutine } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { friendlyActivityText } from "@shared/activity-text";
import { AgentFace, asFaceAgent, type FaceMood } from "@/components/agent-face";

export interface TodayFeedEntry {
  kind: "agent_log" | "pending_approval" | "agent_run";
  ts: string;
  agentLog?: { id: number; agent: string; action: string; status: string };
  agentRun?: { id: number | string; agent: string; status: string };
}

interface ParsedRoutine {
  valid: boolean;
  error?: string;
  name?: string;
  description?: string;
  cronExpression?: string;
  humanSchedule?: string;
  taskType?: string;
  taskConfig?: Record<string, unknown>;
}

const NAMES: Record<string, string> = { draper: "Draper", peggy: "Peggy", pete: "Pete", rogers: "Rogers", harry: "Harry", claw: "Claw" };

const collapseKey = (wsId: number) => `drpr-today-collapsed-${wsId}`;
/** localStorage is a per-viewer convenience: it can be blocked or throw, and the strip works without it. */
function readCollapsed(wsId: number): boolean {
  try {
    const v = localStorage.getItem(collapseKey(wsId));
    if (v !== null) return v === "1";
  } catch { /* fall through to the default */ }
  // Phones start folded so the chat stays in view
  return typeof window !== "undefined" && window.innerWidth < 640;
}
function writeCollapsed(wsId: number, v: boolean) {
  try { localStorage.setItem(collapseKey(wsId), v ? "1" : "0"); } catch { /* blocked: default next time */ }
}

export function greetingNow(d = new Date()): string {
  const h = d.getHours();
  return h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function clock(ts: string): string {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** "Today 4:00 PM" / "Tomorrow 9:00 AM" / "Mon 9:00 AM" */
function whenNext(ts: string | Date | null | undefined): string {
  if (!ts) return "Not scheduled";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "Not scheduled";
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `Today ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} ${time}`;
}

/** The finished things worth telling the owner about, newest first, one line each. */
function doneLines(feed: TodayFeedEntry[]): Array<{ key: string; agent: string; text: string; ts: string }> {
  const seen = new Set<string>();
  const out: Array<{ key: string; agent: string; text: string; ts: string }> = [];
  for (const e of feed) {
    if (e.kind === "agent_log" && e.agentLog) {
      if (e.agentLog.status === "failed") continue;
      const text = friendlyActivityText(e.agentLog.action);
      if (!text || text.startsWith("Working on:") || seen.has(text)) continue;
      seen.add(text);
      out.push({ key: `log-${e.agentLog.id}`, agent: e.agentLog.agent, text, ts: e.ts });
    } else if (e.kind === "agent_run" && e.agentRun?.status === "completed") {
      const who = NAMES[asFaceAgent(e.agentRun.agent)];
      const text = `${who} finished a task you asked for`;
      out.push({ key: `run-${e.agentRun.id}`, agent: e.agentRun.agent, text, ts: e.ts });
    }
  }
  return out;
}

export function TodayWithDraper({ wsId, firstName, mood, feed, pendingCount, onOpenFeed, variant = "strip" }: {
  variant?: "strip" | "column";
  wsId: number;
  firstName: string;
  mood: FaceMood;
  feed: TodayFeedEntry[];
  pendingCount: number;
  onOpenFeed: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isColumn = variant === "column";
  const [stripCollapsed, setCollapsed] = useState(() => readCollapsed(wsId));
  const collapsed = !isColumn && stripCollapsed;
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderText, setReminderText] = useState("");
  const [proposal, setProposal] = useState<ParsedRoutine | null>(null);
  const [reminderError, setReminderError] = useState("");

  const { data: routines = [] } = useQuery<DraperRoutine[]>({
    queryKey: ["/api/workspaces", wsId, "routines"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/routines`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 60000,
  });

  const parse = useMutation({
    mutationFn: async (message: string): Promise<ParsedRoutine> => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/routines/parse`, { message });
      return res.json();
    },
    onSuccess: (p) => {
      if (!p?.valid || !p.cronExpression || !p.taskType) {
        setReminderError(p?.error ? `I couldn't work out when: ${p.error}` : "I couldn't work out when. Try \"Every Monday at 9am, check my new leads\".");
        return;
      }
      setProposal(p);
    },
    onError: (e: any) => setReminderError(e?.message || "I couldn't read that one. Try again in a moment."),
  });

  const create = useMutation({
    mutationFn: async (p: ParsedRoutine) => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/routines`, {
        name: p.name,
        description: p.description || "",
        cronExpression: p.cronExpression,
        taskType: p.taskType,
        taskConfig: p.taskConfig || {},
        deliveryChannel: "both",
        createdFromMessage: reminderText,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "routines"] });
      toast({ title: "Got it", description: "It's on my list. You'll see it under Coming up." });
      closeReminder();
    },
    onError: (e: any) => setReminderError(e?.message || "That didn't save. Try again in a moment."),
  });

  const closeReminder = () => { setReminderOpen(false); setReminderText(""); setProposal(null); setReminderError(""); };
  const submitReminder = () => {
    if (!reminderText.trim()) { setReminderError("Tell me what to do and when, for example \"Every Friday at 4pm, sum up my week\"."); return; }
    setReminderError("");
    setProposal(null);
    parse.mutate(reminderText.trim());
  };
  const toggle = () => { const next = !stripCollapsed; setCollapsed(next); writeCollapsed(wsId, next); };

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const all = doneLines(feed);
  const today = all.filter(l => new Date(l.ts) >= startOfToday);
  const shown = (today.length ? today : all).slice(0, isColumn ? 8 : 4);
  const upcoming = routines
    .filter(r => r.enabled)
    .sort((a, b) => new Date(a.nextRunAt ?? 8.64e15).getTime() - new Date(b.nextRunAt ?? 8.64e15).getTime())
    .slice(0, 3);

  const s = (n: number) => (n === 1 ? "" : "s");
  const summary = [
    mood === "thinking"
      ? "I'm working on something for you right now."
      : today.length
        ? `The team has done ${today.length} thing${s(today.length)} for you today.`
        : "A quiet day so far. I'm keeping an eye on things.",
    pendingCount > 0 ? `${pendingCount} item${s(pendingCount)} ${pendingCount === 1 ? "is" : "are"} waiting for your OK.` : "",
  ].filter(Boolean).join(" ");

  return (
    <section
      className={isColumn
        ? "h-full flex flex-col overflow-y-auto custom-scrollbar"
        : "shrink-0 mx-3 sm:mx-6 mt-3 rounded-2xl border"}
      style={isColumn ? undefined : { background: "var(--chat-surface-bg)", borderColor: "var(--chat-surface-border)" }}
      data-testid={isColumn ? "today-with-draper-column" : "today-with-draper"}
      aria-label="Today with Draper"
    >
      <div className={`flex items-center gap-3 ${isColumn ? "px-4 pt-4 pb-3" : "px-3 sm:px-4 py-3"}`}>
        <AgentFace agent="draper" mood={mood} size={40} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight" data-testid="text-today-greeting">
            {greetingNow()}, {firstName}.
          </p>
          <p className="text-xs text-white/50 leading-snug mt-0.5" data-testid="text-today-summary">{summary}</p>
        </div>
        {pendingCount > 0 && (
          <button
            onClick={onOpenFeed}
            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-bold bg-amber-500/15 border border-amber-500/30 text-amber-400 hover:bg-amber-500/25 transition-all"
            data-testid="button-today-review"
          >
            <Clock size={11} /> Review {pendingCount}
          </button>
        )}
        {!isColumn && <button
          onClick={toggle}
          className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/5 transition-all"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Show today" : "Hide today"}
          data-testid="button-toggle-today"
        >
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>}
      </div>

      {!collapsed && (
        <div
          className={isColumn ? "flex flex-col gap-5 px-4 pb-5 pt-4 border-t" : "grid md:grid-cols-[minmax(0,1fr)_280px] gap-4 px-3 sm:px-4 pb-4 border-t pt-3"}
          style={{ borderColor: "var(--chat-surface-border)" }}
        >
          <div className="min-w-0" data-testid="today-done">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/35 mb-2">{today.length || !all.length ? "Today" : "Lately"}</p>
            {shown.length === 0 ? (
              <p className="text-xs text-white/45 leading-relaxed">
                Nothing to report yet. As soon as Peggy, Pete, Rogers or Harry finish something, it shows up here.
              </p>
            ) : (
              <ul className="space-y-2">
                {shown.map(l => (
                  <li key={l.key} className="flex items-start gap-2">
                    <AgentFace agent={l.agent} size={20} className="mt-px" />
                    <p className="flex-1 min-w-0 text-xs text-white/70 leading-snug line-clamp-2">{l.text}</p>
                    <span className="text-[10px] text-white/30 shrink-0 mt-px">{clock(l.ts)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="min-w-0" data-testid="today-coming-up">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/35">Coming up</p>
              <Link href="/settings?tab=routines" className="text-[10px] text-blue-400 hover:underline" data-testid="link-all-reminders">All reminders</Link>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-xs text-white/45 leading-relaxed mb-2">No reminders yet. Ask me to check on something regularly and I'll do it.</p>
            ) : (
              <ul className="space-y-1.5 mb-2">
                {upcoming.map(r => (
                  <li key={r.id} className="flex items-start gap-2 text-xs" data-testid={`today-routine-${r.id}`}>
                    <Bell size={11} className="text-blue-400 mt-0.5 shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-white/70 truncate">{r.name}</span>
                      <span className="block text-[10px] text-white/35">{whenNext(r.nextRunAt)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {!reminderOpen ? (
              <button
                onClick={() => setReminderOpen(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-dashed border-white/15 text-white/50 hover:text-blue-400 hover:border-blue-500/30 transition-all"
                data-testid="button-today-remind"
              >
                <Plus size={11} /> Remind me…
              </button>
            ) : (
              <div className="space-y-2" data-testid="today-reminder-form">
                {!proposal ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <input
                        autoFocus
                        value={reminderText}
                        onChange={e => { setReminderText(e.target.value); if (reminderError) setReminderError(""); }}
                        onKeyDown={e => { if (e.key === "Enter") submitReminder(); if (e.key === "Escape") closeReminder(); }}
                        placeholder="Every Monday at 9am, check my new leads"
                        className="flex-1 min-w-0 rounded-lg px-2.5 py-1.5 text-xs text-white/80 placeholder:text-white/30 outline-none"
                        style={{ background: "var(--chat-input-bg)", border: "1px solid var(--chat-input-border)" }}
                        data-testid="input-today-reminder"
                      />
                      <button onClick={closeReminder} className="text-white/35 hover:text-white/60" aria-label="Cancel" data-testid="button-today-reminder-cancel"><X size={13} /></button>
                    </div>
                    <button
                      onClick={submitReminder}
                      disabled={parse.isPending}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-500/15 border border-blue-500/30 text-blue-400 hover:bg-blue-500/25 transition-all"
                      data-testid="button-today-reminder-next"
                    >
                      {parse.isPending && <Loader2 size={10} className="animate-spin" />} Next
                    </button>
                  </>
                ) : (
                  <div className="rounded-lg px-2.5 py-2 text-xs" style={{ background: "var(--chat-input-bg)", border: "1px solid var(--chat-input-border)" }} data-testid="today-reminder-proposal">
                    <p className="text-white/70">Here's what I'll do:</p>
                    <p className="text-white/90 font-semibold mt-0.5">{proposal.name}</p>
                    {proposal.humanSchedule && <p className="text-[11px] text-white/50">{proposal.humanSchedule}</p>}
                    <div className="flex gap-1.5 mt-2">
                      <button
                        onClick={() => create.mutate(proposal)}
                        disabled={create.isPending}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25 transition-all"
                        data-testid="button-today-reminder-save"
                      >
                        {create.isPending && <Loader2 size={10} className="animate-spin" />} Looks right
                      </button>
                      <button
                        onClick={() => setProposal(null)}
                        className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-white/10 text-white/50 hover:text-white/70 transition-all"
                        data-testid="button-today-reminder-edit"
                      >
                        Change it
                      </button>
                    </div>
                  </div>
                )}
                {reminderError && <p className="text-[11px] text-red-400" role="alert" data-testid="text-today-reminder-error">{reminderError}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
